import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'motion/react'
import { Button } from '@/components/ui'
import { useScrollReveal, useStaggeredGrid, useHeroReveal } from '@gjl/motion/css'
import { Check, ArrowRight, Users, Tag, Image, Globe, Filter, Layout } from 'lucide-react'
import { APP_URL } from '@/lib/public-trpc'
import { capability } from '@gjl/voice/pricing'

const websiteCapability = capability('website')!

const features = [
  {
    icon: Users,
    title: 'People Profiles',
    description:
      'Create profiles for musicians, staff, board members, volunteers, and guest artists. Each person gets their own page with bio and photo.',
  },
  {
    icon: Tag,
    title: 'Categories & Tags',
    description:
      'Organize people by role: musicians, staff, board, volunteers. Tag by instrument, voice part, or department.',
  },
  {
    icon: Image,
    title: 'Photo Gallery',
    description: 'Upload headshots and performance photos. Display people beautifully on your public website.',
  },
  {
    icon: Globe,
    title: 'Public Directory',
    description: 'Show your team on your website automatically. Let audiences see who makes your organization run.',
  },
  {
    icon: Filter,
    title: 'Flexible Display',
    description:
      'Choose what to show publicly. Some people on the website, others internal-only. You control the visibility.',
  },
  {
    icon: Layout,
    title: 'Website Integration',
    description:
      'People appear on your About page automatically. No duplicate data entry—update once, show everywhere.',
  },
]

const useCases = [
  {
    title: 'Musicians & Artists',
    description:
      'Showcase your performers with bios, photos, and instruments. Guest artists and regular members in one place.',
  },
  {
    title: 'Staff & Leadership',
    description:
      'Feature your music director, executive director, and administrative team. Show who runs your organization.',
  },
  {
    title: 'Board of Directors',
    description: 'List board members with titles and affiliations. Professional presentation for donors and grantors.',
  },
  {
    title: 'Volunteers',
    description: 'Recognize the people who make your concerts happen. Ushers, stage crew, committee members.',
  },
]

const otherFeatures = [
  { title: 'Website', href: '/platform/features/website', description: 'Build your site' },
  { title: 'Events', href: '/platform/features/events', description: 'Manage concerts' },
  { title: 'Email', href: '/platform/features/email', description: 'Send newsletters' },
  { title: 'Donations', href: '/platform/features/donations', description: 'Accept support' },
]

export default function TeamFeaturePage() {
  useEffect(() => {
    document.title = 'Team & People Management for Music Organizations | Fugue Lab App'
  }, [])

  // Animation refs
  const heroRef = useHeroReveal<HTMLDivElement>(0.2)
  const featuresRef = useStaggeredGrid<HTMLDivElement>('.feature-card', { stagger: 0.1 })
  const useCasesRef = useStaggeredGrid<HTMLDivElement>('.use-case-card', { stagger: 0.1 })
  const pricingRef = useStaggeredGrid<HTMLDivElement>('.pricing-card', { stagger: 0.15 })
  const otherFeaturesRef = useStaggeredGrid<HTMLDivElement>('.other-feature-card', { stagger: 0.1 })
  const ctaRef = useScrollReveal<HTMLDivElement>('.cta-content')

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="px-4 py-20">
        <div ref={heroRef} className="container mx-auto max-w-4xl text-center">
          <div className="bg-primary/10 text-primary mb-6 inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium">
            <Users className="h-4 w-4" />
            People Management
          </div>
          <h1 className="mb-6 text-4xl font-bold md:text-5xl">
            Showcase the people
            <br />
            behind the music
          </h1>
          <p className="text-muted-foreground mx-auto mb-8 max-w-2xl text-xl">
            Feature your musicians, staff, board members, and volunteers with professional profiles. Keep your website
            current with the people who make your organization great.
          </p>
          <div className="flex flex-col justify-center gap-4 sm:flex-row">
            <a href={`${APP_URL}/auth`}>
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
              >
                <Button size="lg" className="cursor-pointer px-8 text-lg">
                  Add Your Team <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </motion.div>
            </a>
            <Link to="/platform">
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
              >
                <Button variant="outline" size="lg" className="cursor-pointer px-8 text-lg">
                  See All Features
                </Button>
              </motion.div>
            </Link>
          </div>
          <p className="text-muted-foreground mt-4 text-sm">Included with every Fugue Lab website.</p>
        </div>
      </section>

      {/* Features Grid */}
      <section className="bg-muted/30 px-4 py-20">
        <div className="container mx-auto max-w-6xl">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl font-bold">Manage Your Entire Team</h2>
            <p className="text-muted-foreground mx-auto max-w-2xl">
              From musicians to board members, everyone has a place.
            </p>
          </div>
          <div ref={featuresRef} className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {features.map((feature) => (
              <motion.div
                key={feature.title}
                className="feature-card border-border bg-card rounded-lg border p-6"
                whileHover={{ y: -4 }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              >
                <feature.icon className="text-primary mb-4 h-10 w-10" />
                <h3 className="mb-2 text-lg font-semibold">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="px-4 py-20">
        <div className="container mx-auto max-w-4xl">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl font-bold">For Everyone in Your Organization</h2>
            <p className="text-muted-foreground">Different roles, one system to manage them all.</p>
          </div>
          <div ref={useCasesRef} className="grid gap-8 md:grid-cols-2">
            {useCases.map((useCase) => (
              <motion.div
                key={useCase.title}
                className="use-case-card border-border bg-card rounded-lg border p-6"
                whileHover={{ y: -4 }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              >
                <h3 className="mb-2 text-lg font-semibold">{useCase.title}</h3>
                <p className="text-muted-foreground">{useCase.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Profile Preview */}
      <section className="bg-muted/30 px-4 py-20">
        <div className="container mx-auto max-w-4xl">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl font-bold">Professional Profiles</h2>
            <p className="text-muted-foreground">Every person gets a beautiful profile page.</p>
          </div>
          <div className="bg-card border-border rounded-2xl border p-8">
            <div className="flex flex-col gap-8 md:flex-row">
              <div className="flex-shrink-0">
                <div className="bg-muted flex h-32 w-32 items-center justify-center rounded-full">
                  <Users className="text-muted-foreground h-12 w-12" />
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <h3 className="text-2xl font-bold">Sarah Johnson</h3>
                  <p className="text-primary">Music Director</p>
                </div>
                <p className="text-muted-foreground">
                  Sarah has served as Music Director since 2018, leading our ensemble through acclaimed performances of
                  both standard repertoire and contemporary works. Prior to joining us, she held positions with the
                  Metropolitan Symphony and served as Associate Conductor at the Chicago Opera Theatre.
                </p>
                <div className="flex gap-2">
                  <span className="bg-muted rounded-full px-3 py-1 text-sm">Leadership</span>
                  <span className="bg-muted rounded-full px-3 py-1 text-sm">Staff</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="px-4 py-20">
        <div className="container mx-auto max-w-4xl">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl font-bold">Simple Pricing</h2>
            <p className="text-muted-foreground">Team roles come with your website. No separate charge.</p>
          </div>
          <div ref={pricingRef} className="mx-auto max-w-md">
            <motion.div
              className="pricing-card border-primary/50 bg-primary/5 rounded-2xl border p-8"
              whileHover={{ y: -4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <div className="text-primary mb-2 text-sm font-medium">No separate charge</div>
              <h3 className="mb-2 text-2xl font-bold">Included with {websiteCapability.name}</h3>
              <div className="mb-4 flex items-baseline gap-1">
                <span className="text-4xl font-bold">${websiteCapability.priceMonthly}</span>
                <span className="text-muted-foreground">/month</span>
              </div>
              <p className="text-muted-foreground mb-6">
                Team roles and people profiles are part of every Fugue Lab website. There is no separate charge.
              </p>
              <ul className="mb-8 space-y-3">
                <li className="flex items-start gap-3">
                  <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" />
                  <span className="text-muted-foreground">Unlimited people profiles</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" />
                  <span className="text-muted-foreground">Photos, categories, and tags</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" />
                  <span className="text-muted-foreground">Public directory on your site</span>
                </li>
              </ul>
              <Link to="/packages" className="block">
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                >
                  <Button className="w-full cursor-pointer">See All Pricing</Button>
                </motion.div>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Other Features */}
      <section className="bg-muted/30 px-4 py-16">
        <div className="container mx-auto max-w-4xl">
          <div className="mb-8 text-center">
            <h2 className="text-2xl font-bold">More Features</h2>
            <p className="text-muted-foreground">People management is just the beginning.</p>
          </div>
          <div ref={otherFeaturesRef} className="grid grid-cols-2 gap-4 md:grid-cols-4">
            {otherFeatures.map((feature) => (
              <motion.div
                key={feature.href}
                className="other-feature-card"
                whileHover={{ y: -4 }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              >
                <Link
                  to={feature.href}
                  className="border-border bg-card hover:border-primary block h-full rounded-lg border p-4 text-center transition-colors"
                >
                  <h3 className="font-semibold">{feature.title}</h3>
                  <p className="text-muted-foreground text-sm">{feature.description}</p>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary/5 px-4 py-16">
        <div ref={ctaRef} className="container mx-auto max-w-3xl text-center">
          <div className="cta-content">
            <h2 className="mb-4 text-3xl font-bold">Ready to showcase your team?</h2>
            <p className="text-muted-foreground mb-8 text-xl">
              Join music organizations using Fugue Lab to feature their people.
            </p>
            <a href={`${APP_URL}/auth`}>
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                className="inline-block"
              >
                <Button size="lg" className="cursor-pointer px-8 text-lg">
                  Add Your Team <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </motion.div>
            </a>
          </div>
        </div>
      </section>
    </div>
  )
}
